import { RefObject, useCallback, useEffect, useState } from "react";
import { useTreeViewNodeNavigation } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/TreeViewNodeNavigationProvider";

const FOCUS_ANIMATION_CLASS = "RateBuilderTreeItemContent-focused";

interface UseTreeItemFocusOptions {
  itemId: string;
  elementRef: RefObject<HTMLElement | null>;
}

interface UseTreeItemFocusReturn {
  focused: boolean;
  addFocusAnimation: () => void;
  clearFocusAnimation: () => void;
}

/**
 * Custom hook for managing tree item focus animations and scroll behavior.
 * Handles smooth scrolling to focused items and CSS animation classes.
 */
export const useTreeItemFocus = ({ itemId, elementRef }: UseTreeItemFocusOptions): UseTreeItemFocusReturn => {
  const [focused, setFocused] = useState(false);
  const { focusedItem, cancelFocused } = useTreeViewNodeNavigation();

  const addFocusAnimation = useCallback(() => {
    const element = elementRef.current;
    if (element) {
      element.classList.remove(FOCUS_ANIMATION_CLASS);
      setTimeout(() => element.classList.add(FOCUS_ANIMATION_CLASS), 0);
    }
  }, [elementRef]);

  const clearFocusAnimation = useCallback(() => {
    elementRef.current?.classList.remove(FOCUS_ANIMATION_CLASS);
  }, [elementRef]);

  const setupScrollBehavior = useCallback(() => {
    if (window.onscrollend === undefined) {
      console.warn("Your browser doesn't support the scrollend event");
      addFocusAnimation();
    } else {
      removeEventListener("scrollend", addFocusAnimation);
      addEventListener("scrollend", addFocusAnimation, { once: true });
    }
  }, [addFocusAnimation]);

  const scrollToItem = useCallback(
    (element: HTMLElement) => {
      setTimeout(() => {
        const initTop = element.getBoundingClientRect().top;
        element.style.scrollMarginTop = "calc(var(--sinch-header-height, 56px) + 50px)";
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => {
          const endTop = element.getBoundingClientRect().top;
          if (initTop === endTop) {
            removeEventListener("scrollend", addFocusAnimation);
            addFocusAnimation();
          }
        }, 100);
      }, 0);
    },
    [addFocusAnimation]
  );

  // Focus management effect
  useEffect(() => {
    const containerEl = elementRef.current;
    const shouldFocus = focusedItem === itemId && containerEl && !focused;
    const shouldUnfocus = focusedItem !== itemId && focused;

    if (shouldFocus) {
      setFocused(true);
      cancelFocused();

      // Setup animation listeners
      removeEventListener("animationend", clearFocusAnimation);
      addEventListener("animationend", clearFocusAnimation, { once: true });

      setupScrollBehavior();
      scrollToItem(containerEl);
    } else if (shouldUnfocus) {
      // Part of the imperative focus/scroll orchestration driven by tree navigation events.
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect
      setFocused(false);
    }
  }, [focusedItem, itemId, focused, cancelFocused, clearFocusAnimation, setupScrollBehavior, scrollToItem, elementRef]);

  return {
    focused,
    addFocusAnimation,
    clearFocusAnimation,
  };
};
